/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import {
    Box,
    SimpleGrid,
    VStack,
    Text,
    Button,
    Center,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {cssColorGroups} from '@salesforce/retail-react-app/app/constants'
import {useIntl} from 'react-intl'
import {
    ADD_FILTER_HIT_COUNT,
    REMOVE_FILTER_HIT_COUNT
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {accordionListBtnStyles} from './refinements.style'
import useScrollToMoreButton from '../../../hooks/useScrollToMoreButton'

const ColorRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant: 'circle',
        disabled: false
    })

    const [showAll, setShowAll] = useState(false)
    const {setScrollToMore, moreButtonRef} = useScrollToMoreButton()

    const getInitialSliceCount = (attribute) => {
        switch (attribute) {
            case 'c_refinementColor':
                return 12
            default:
                return filter.values.length
        }
    }

    const initialSliceCount = getInitialSliceCount(filter.attributeId)
    const displayedValues = showAll ? filter.values : filter.values.slice(0, initialSliceCount)

    const getToggleMessage = () => {
        return showAll ? `Less ${filter.label}s` : `More ${filter.label}s`
    }

    const toggleShowAllFilters = () => {
        setScrollToMore(!!showAll)
        setShowAll(!showAll)
    }

    return (
        <Box>
            <SimpleGrid columns={4} spacingY={{base: '10px', md: '15px'}}>
                {displayedValues?.map((value, idx) => {
                    const isSelected = selectedFilters.includes(value.value)

                    // Don't display refinements with no results, unless we got there by selecting too
                    // many refinements
                    if (value.hitCount === 0 && !isSelected) return

                    return (
                        <Box key={idx}>
                            <VStack
                                onClick={() => toggleFilter(value, filter.attributeId, isSelected)}
                                spacing={1}
                                cursor="pointer"
                            >
                                <Button
                                    {...styles.swatch}
                                    color={isSelected ? 'black' : 'gray.200'}
                                    border={isSelected ? '1px' : '0'}
                                    aria-checked={isSelected}
                                    variant="outline"
                                    marginRight={0}
                                    marginBottom="-1px"
                                    padding="2px"
                                    minWidth="31px"
                                    minHeight="31px"
                                    height="auto"
                                    width="auto"
                                    aria-label={intl.formatMessage(
                                        isSelected ? REMOVE_FILTER_HIT_COUNT : ADD_FILTER_HIT_COUNT,
                                        value
                                    )}
                                >
                                    <Center
                                        {...styles.swatchButton}
                                        marginRight={0}
                                        border="1px solid black"
                                        height="25px"
                                        width="25px"
                                    >
                                        <Box
                                            marginRight={0}
                                            height="100%"
                                            width="100%"
                                            backgroundRepeat="no-repeat"
                                            backgroundSize="cover"
                                            background={
                                                cssColorGroups[value?.presentationId?.toLowerCase()]
                                            }
                                        />
                                    </Center>
                                </Button>
                                <Text
                                    display="flex"
                                    alignItems="center"
                                    textTransform="uppercase"
                                    fontSize="xs"
                                    marginTop="7px !important"
                                    textAlign="center"
                                    letterSpacing="1px"
                                    lineHeight="normal"
                                    marginBottom="1px"
                                    aria-hidden="true" // avoid redundant readout since swatch has aria label
                                >
                                    {intl.formatMessage(
                                        {
                                            id: 'colorRefinements.label.colorLabel',
                                            defaultMessage: '{colorLabel}'
                                        },
                                        {colorLabel: value.label}
                                    )}
                                </Text>
                            </VStack>
                        </Box>
                    )
                })}
            </SimpleGrid>
            {filter.values.length > initialSliceCount && (
                <Box ref={moreButtonRef} onClick={toggleShowAllFilters} sx={accordionListBtnStyles}>
                    {getToggleMessage()}
                </Box>
            )}
        </Box>
    )
}

ColorRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array
}

export default ColorRefinements
